import { Link } from 'react-router-dom';
import { useAllCategories } from '../../hooks/useAllCategories';
import { useDragScroll } from '../../hooks/useDragScroll';
import { pickFeaturedCategories } from '../../utils/featuredCategories';
import { AGE_BUCKETS, getAgeBucketHref } from '../../constants/ageBuckets';

const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/EJYNFrfY385GkobvhkPUcK?s=sw&p=i&mlu=4&ilr=4&iam=0';
const GENDER_MASCULINO = 1;
const GENDER_FEMININO = 2;
const ICON_COUNT = 11;
const HIDDEN_CATEGORY_PATTERN = /^blusa/;

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full font-bold text-base hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate-dark focus-visible:ring-offset-2 focus-visible:ring-offset-champagne-light';
const SHADOW_FILLED = 'sm:shadow-[0_6px_14px_-6px_rgba(168,90,16,0.55)]';
const GENDER_BUTTON = `${BUTTON_BASE} flex-1 sm:flex-none bg-white border-2 border-chocolate-dark text-chocolate-dark hover:bg-champagne sm:bg-chocolate-dark sm:text-white sm:hover:bg-chocolate-deep ${SHADOW_FILLED}`;

function BellIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  );
}

export function HomeIntro() {
  const { categories: allCategories } = useAllCategories();
  const categories = pickFeaturedCategories(allCategories)
    .map((category, index) => ({ category, iconIndex: index }))
    .filter(({ category }) => !HIDDEN_CATEGORY_PATTERN.test(category.name.normalize('NFD').toLowerCase().trim()));
  const { ref, onMouseDown, onMouseMove, onMouseUp, onMouseLeave, onMouseEnter, onTouchStart, onTouchEnd, onClickCapture } =
    useDragScroll<HTMLDivElement>();

  return (
    <section aria-labelledby="home-intro-title" className="max-w-[1600px] mx-auto px-4 pt-6 md:pt-8">
      <div className="relative overflow-hidden rounded-[2rem] bg-champagne-light border border-champagne-dark">
        <div
          className="absolute inset-0 opacity-15 mix-blend-multiply pointer-events-none"
          style={{ backgroundImage: "url('/images/brand/pattern-bg.webp')", backgroundSize: '260px auto' }}
          aria-hidden="true"
        />

        <div className="relative grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-6 lg:gap-10 xl:gap-12 items-center p-5 sm:p-8 lg:p-10">
          <div className="home-rise flex flex-col gap-4 min-w-0">
            <h2 id="home-intro-title" className="font-brand text-2xl sm:text-3xl text-text-dark leading-tight tracking-tight text-balance">
              Por onde começar?
            </h2>

            <div className="order-2 sm:order-1 flex flex-wrap gap-3">
              <Link to={`/catalogo?gender=${GENDER_FEMININO}`} className={GENDER_BUTTON}>
                Meninas
              </Link>
              <Link to={`/catalogo?gender=${GENDER_MASCULINO}`} className={GENDER_BUTTON}>
                Meninos
              </Link>
              <Link to="/catalogo?sort=1" className={`${BUTTON_BASE} hidden sm:inline-flex bg-white hover:bg-champagne-light text-chocolate-dark border-2 border-chocolate-dark`}>
                Novidades
                <ArrowIcon />
              </Link>
            </div>

            <div className="order-1 sm:order-2 flex flex-wrap items-center gap-2">
              <span className="w-full sm:w-auto text-base sm:text-sm font-bold text-text-dark mr-1">Por idade</span>
              {AGE_BUCKETS.map((bucket) => (
                <Link
                  key={bucket.key}
                  to={getAgeBucketHref(bucket)}
                  className="inline-flex items-center h-11 sm:h-10 px-4 rounded-full bg-white hover:bg-champagne-light border-2 sm:border border-chocolate-dark/60 sm:border-champagne-dark text-sm font-bold sm:font-semibold text-text-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate-dark"
                >
                  {bucket.label}
                </Link>
              ))}
            </div>

            <a
              href={WHATSAPP_GROUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="order-3 group inline-flex items-center gap-3 self-start h-12 sm:h-14 focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate-dark focus-visible:ring-offset-2 focus-visible:ring-offset-champagne-light pl-2 pr-6 rounded-full bg-white border-2 border-chocolate-dark text-chocolate-dark hover:bg-champagne sm:bg-chocolate-dark sm:text-white sm:hover:bg-chocolate-deep font-bold text-base sm:shadow-[0_6px_14px_-6px_rgba(168,90,16,0.55)] hover:-translate-y-0.5 transition-all duration-200"
            >
              <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-chocolate-dark/10 sm:bg-white/20 flex items-center justify-center group-hover:rotate-12 transition-transform duration-200">
                <BellIcon />
              </span>
              Entrar no Grupo Vip
            </a>
          </div>

          {categories.length > 0 && (
            <div
              ref={ref}
              className="flex gap-3 sm:gap-4 lg:gap-2 xl:gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 sm:-mx-8 sm:px-8 lg:mx-0 lg:px-0 lg:overflow-visible py-2 [@media(pointer:fine)]:cursor-grab [@media(pointer:fine)]:lg:cursor-auto [@media(pointer:fine)]:select-none"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave}
              onMouseEnter={onMouseEnter}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              onClickCapture={onClickCapture}
            >
              {categories.map(({ category, iconIndex }, index) => (
                <Link
                  key={category.id}
                  to={`/catalogo?cat=${category.id}`}
                  style={{ animationDelay: `${120 + index * 45}ms` }}
                  className="home-rise group w-[4.75rem] sm:w-20 lg:w-auto lg:flex-1 lg:min-w-0 shrink-0 flex flex-col items-center gap-2 text-center rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate focus-visible:ring-offset-2 focus-visible:ring-offset-champagne-light"
                >
                  <span className="w-14 h-14 sm:w-16 sm:h-16 lg:w-12 lg:h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 rounded-full bg-white border-2 border-champagne-dark flex items-center justify-center shadow-[0_6px_14px_-8px_rgba(60,36,21,0.45)] group-hover:border-chocolate group-hover:-translate-y-0.5 transition-all duration-200">
                    <img
                      src={`/images/categories/icon-${(iconIndex % ICON_COUNT) + 1}.webp`}
                      alt=""
                      draggable={false}
                      className="w-10 h-10 sm:w-11 sm:h-11 lg:w-9 lg:h-9 xl:w-10 xl:h-10 2xl:w-11 2xl:h-11 group-hover:scale-110 transition-transform duration-200"
                    />
                  </span>
                  <span className="text-xs sm:text-sm lg:text-xs xl:text-sm font-bold text-text-dark leading-tight break-words [hyphens:auto] text-balance line-clamp-2">{category.name}</span>
                </Link>
              ))}
              <Link
                to="/catalogo"
                style={{ animationDelay: `${120 + categories.length * 45}ms` }}
                className="home-rise group w-[4.75rem] sm:w-20 lg:w-auto lg:flex-1 lg:min-w-0 shrink-0 flex flex-col items-center gap-2 text-center rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate focus-visible:ring-offset-2 focus-visible:ring-offset-champagne-light"
              >
                <span className="w-14 h-14 sm:w-16 sm:h-16 lg:w-12 lg:h-12 xl:w-14 xl:h-14 2xl:w-16 2xl:h-16 rounded-full bg-chocolate-dark text-white flex items-center justify-center shadow-[0_6px_14px_-8px_rgba(60,36,21,0.6)] group-hover:bg-chocolate-deep group-hover:-translate-y-0.5 transition-all duration-200">
                  <ArrowIcon />
                </span>
                <span className="text-xs sm:text-sm lg:text-xs xl:text-sm font-bold text-text-dark leading-tight break-words [hyphens:auto]">Ver tudo</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
